import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { NumericInputProps } from './types';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import Input from '../Input/Input';
import { AddOn } from './styles';

const NumericInput: React.FC<NumericInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    label,
    readOnly,
    disabled,
    testId,
    size,
    variant = 'underline',
    helperText,
    showClearIcon,
    value,
    allowDecimal = true,
    negative,
    leftAddOn,
    rightAddOn,
    maxInt = 15,
    maxDecimal = allowDecimal ? 2 : null,
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
  } = props;
  const { actualTheme } = useContext(ThemeContext);

  const decimalSeparators = ['.', ','];

  const maxLength = useMemo(() => (allowDecimal ? maxInt + maxDecimal + 1 : maxInt), [
    allowDecimal,
    maxDecimal,
    maxInt,
  ]);

  if (!allowDecimal && maxDecimal) {
    throw new Error("The NumericInput component can't have maxDecimal if allowDecimal is false.");
  }
  const [internalValue, setInternalValue] = useState({
    internalValue: input ? input.value : value,
    isActive: false,
  });

  const lastValue = useRef('');

  const decimalRegex = useMemo(
    () =>
      new RegExp(
        `^[0-9]${maxInt ? `{0,${maxInt}}` : '*'}[.,]?[0-9]${
          maxDecimal ? `{0,${maxDecimal}}` : '*'
        }$`,
      ),
    [maxDecimal, maxInt],
  );

  const validDecimal = useCallback(
    (valueInput: string) =>
      valueInput && decimalRegex.test(valueInput) && valueInput.length <= maxLength,
    [decimalRegex, maxLength],
  );

  const handleNumberValidation = useCallback(
    (valueInput: string) => {
      let emitValue;
      if (allowDecimal && validDecimal(valueInput)) {
        const noComaValue = valueInput.replace(',', '.');
        emitValue = parseFloat(noComaValue);
      } else if (/^\d+\d*$/.test(valueInput) && valueInput.length <= maxLength) {
        emitValue = parseFloat(valueInput);
      }

      emitValue = negative && valueInput.length <= maxLength ? Math.abs(emitValue) : emitValue;

      return emitValue ?? '';
    },
    [allowDecimal, maxLength, negative, validDecimal],
  );

  const format = useCallback(
    (val: string) => {
      const lastLen = lastValue.current.length;
      let formattedValue = val;
      if (allowDecimal) {
        formattedValue = formattedValue.replace(',', '.');
        if (
          formattedValue.length === maxInt &&
          lastLen !== maxInt + 1 &&
          !formattedValue.includes('.')
        ) {
          return `${formattedValue}.`;
        }
        if (
          formattedValue.length === maxInt + 1 &&
          lastLen === maxInt &&
          !formattedValue.includes('.')
        ) {
          const value1 = formattedValue.slice(0, maxInt);
          const value2 = formattedValue.slice(maxInt, maxInt + 1);
          return `${value1}.${value2}`;
        }
        if (
          formattedValue.length < lastLen &&
          lastValue.current.includes('.') &&
          !formattedValue.includes('.')
        ) {
          return formattedValue.substring(0, maxInt);
        }
      }
      return formattedValue;
    },
    [allowDecimal, maxInt],
  );

  const handleChange = useCallback(
    (newValue: string) => {
      const checkedValue = handleNumberValidation(newValue);
      if (newValue === '' || typeof checkedValue === 'number') {
        const formattedValue = format(newValue);
        setInternalValue({
          internalValue: formattedValue,
          isActive: true,
        });
        lastValue.current = formattedValue;

        if (input) {
          input.onChange(
            Number.isNaN(parseFloat(formattedValue)) ? '' : parseFloat(formattedValue),
          );
        }
        if (onChange) {
          onChange(Number.isNaN(parseFloat(formattedValue)) ? '' : parseFloat(formattedValue));
        }
      }
    },
    [format, handleNumberValidation, input, onChange],
  );

  const handleClear = useCallback(() => {
    if (input) {
      input.onChange('');
    }
    if (onChange) onChange('');
  }, [input, onChange]);

  const handleBlur = useCallback(() => {
    if (decimalSeparators.includes(internalValue.internalValue)) {
      setInternalValue({
        internalValue: 0,
        isActive: false,
      });
      if (input) input.onChange(0);
      onChange(0);
    } else {
      setInternalValue({
        internalValue: input ? input.value : value,
        isActive: false,
      });
    }
    if (input) {
      input.onBlur();
    }
    if (onBlur) onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, onBlur, onChange, value]);

  const handleFocus = useCallback(() => {
    setInternalValue({
      internalValue: input ? input.value : value,
      isActive: true,
    });
    if (onFocus) onFocus();
  }, [input, onFocus, value]);

  const variableToShow = useMemo(
    () => (internalValue.isActive ? internalValue.internalValue.toString() : input.value.toString()),
    [input.value, internalValue.internalValue, internalValue.isActive],
  );

  return (
    <ThemeProvider theme={actualTheme}>
      <Input
        testId={testId}
        label={label}
        readOnly={readOnly || disabled}
        disabled={disabled}
        value={variableToShow}
        input={input}
        meta={meta}
        autoComplete="off"
        size={size}
        variant={variant}
        helperText={helperText}
        showClearIcon={showClearIcon}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={handleChange}
        onClear={handleClear}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onMouseDown={onMouseDown}
        leftAddOn={leftAddOn && <AddOn>{leftAddOn}</AddOn>}
        rightAddOn={rightAddOn && <AddOn>{rightAddOn}</AddOn>}
      />
    </ThemeProvider>
  );
};

export default NumericInput;
